import { useState } from 'react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  Input,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useCurrentEmployee,
  useUpdateCurrentEmployee,
  type Employee,
  type UpdateCurrentEmployeePayload,
} from '@/api/account';
import { formatDate } from '@/lib/format';
import { InfoGrid, type InfoItem } from './components/InfoGrid';
import { AccountIdentityHeader } from './components/AccountIdentityHeader';
import { AccountBreadcrumbs } from './components/account-breadcrumbs';

/**
 * Personal profile for the signed-in employee. Personal / Identification /
 * Employment tabs are read-only. Contact and Address tabs are inline-editable
 * via PUT /employees/current. Real data via useCurrentEmployee.
 */
export default function AccountInfoPage() {
  const { data: emp, isLoading, isError, refetch } = useCurrentEmployee();

  return (
    <Container size="full">
      <PageHeader
        breadcrumbs={<AccountBreadcrumbs current="Personal Info" />}
        title="My Account"
        description="Your detailed personal information"
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading || !emp ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <AccountInfoBody emp={emp} />
      )}
    </Container>
  );
}

/* ── Read-only body ─────────────────────────────────────────────────────── */

function AccountInfoBody({ emp }: { emp: Employee }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <AccountIdentityHeader />

      <Card>
        <CardBody>
          <Tabs defaultValue="personal">
            <TabsList>
              <TabsTrigger value="personal">Personal</TabsTrigger>
              <TabsTrigger value="identification">Identification</TabsTrigger>
              <TabsTrigger value="contact">Contact</TabsTrigger>
              <TabsTrigger value="address">Address</TabsTrigger>
              <TabsTrigger value="employment">Employment</TabsTrigger>
            </TabsList>

            <TabsContent value="personal">
              <InfoGrid items={personalItems(emp)} />
            </TabsContent>

            <TabsContent value="identification">
              <InfoGrid items={identificationItems(emp)} />
            </TabsContent>

            <TabsContent value="contact">
              <ContactEditTab emp={emp} />
            </TabsContent>

            <TabsContent value="address">
              <AddressEditTab emp={emp} />
            </TabsContent>

            <TabsContent value="employment">
              <InfoGrid items={employmentItems(emp)} />
            </TabsContent>
          </Tabs>
        </CardBody>
      </Card>
    </div>
  );
}

/* ── Read-only item builders ─────────────────────────────────────────────── */

function personalItems(emp: Employee): InfoItem[] {
  return [
    { label: 'Full name', value: emp.employee_name },
    { label: 'Employee code', value: emp.employee_code },
    { label: 'Gender', value: emp.employee_gender },
    { label: 'Birthday', value: formatDate(emp.employee_birthday) },
    { label: 'Marriage', value: emp.employee_marriage },
    { label: 'Nation', value: emp.employee_nation },
    { label: 'Nationality', value: emp.employee_nationality },
    { label: 'Religion', value: emp.employee_religion },
  ];
}

function identificationItems(emp: Employee): InfoItem[] {
  return [
    { label: 'ID number', value: emp.employee_idno },
    { label: 'Issued on', value: formatDate(emp.employee_idno_issued_on) },
    { label: 'Issued by', value: emp.employee_idno_issued_by },
  ];
}

function employmentItems(emp: Employee): InfoItem[] {
  return [
    { label: 'Department', value: emp.department_name },
    { label: 'Position', value: emp.position_name },
    { label: 'Contract type', value: emp.employee_contract_type },
    { label: 'Seniority level', value: emp.employee_seniority_level },
    { label: 'Start date', value: formatDate(emp.employee_start_date) },
    { label: 'End date', value: formatDate(emp.employee_end_date) },
    { label: 'Day-off left', value: emp.employee_day_off_left },
  ];
}

/* ── Inline-edit helpers ─────────────────────────────────────────────────── */

/** Build the full PUT payload from the current employee + any local overrides. */
function buildPayload(emp: Employee, overrides: Partial<UpdateCurrentEmployeePayload>): UpdateCurrentEmployeePayload {
  return {
    employee_name: emp.employee_name ?? '',
    employee_gender: emp.employee_gender ?? '',
    employee_birthday: emp.employee_birthday ?? '',
    employee_marriage: emp.employee_marriage ?? '',
    employee_idno: emp.employee_idno ?? '',
    employee_idno_issued_by: emp.employee_idno_issued_by ?? '',
    employee_idno_issued_on: emp.employee_idno_issued_on ?? '',
    employee_phone_number: emp.employee_phone_number ?? '',
    employee_email: emp.employee_email ?? '',
    employee_street: emp.employee_street ?? '',
    employee_city: emp.employee_city ?? '',
    employee_district: emp.employee_district ?? '',
    employee_ward: emp.employee_ward ?? '',
    employee_temporary_street: emp.employee_temporary_street ?? '',
    employee_temporary_city: '',
    employee_temporary_district: '',
    employee_temporary_ward: '',
    ...overrides,
  };
}

/* ── Contact edit tab ─────────────────────────────────────────────────────── */

function ContactEditTab({ emp }: { emp: Employee }) {
  const mutation = useUpdateCurrentEmployee();
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(emp.employee_phone_number ?? '');
  const [email, setEmail] = useState(emp.employee_email ?? '');
  const [saveError, setSaveError] = useState('');

  const handleSave = async () => {
    setSaveError('');
    try {
      await mutation.mutateAsync(buildPayload(emp, {
        employee_phone_number: phone,
        employee_email: email,
      }));
      setEditing(false);
    } catch {
      setSaveError('Failed to save. Please try again.');
    }
  };

  const handleCancel = () => {
    setPhone(emp.employee_phone_number ?? '');
    setEmail(emp.employee_email ?? '');
    setSaveError('');
    setEditing(false);
  };

  if (!editing) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <InfoGrid items={[
          { label: 'Phone', value: emp.employee_phone_number },
          { label: 'Personal email', value: emp.employee_email },
          { label: 'Company email', value: emp.employee_email_company },
        ]} />
        <div>
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Edit contact info
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 480 }}>
      <Field label="Phone number">
        {(p) => (
          <Input {...p} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" />
        )}
      </Field>
      <Field label="Personal email">
        {(p) => (
          <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Personal email" />
        )}
      </Field>
      {saveError && (
        <Alert tone="danger" title={saveError} />
      )}
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
        <Button onClick={handleSave} loading={mutation.isPending}>
          Save
        </Button>
        <Button variant="secondary" onClick={handleCancel} disabled={mutation.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ── Address edit tab ─────────────────────────────────────────────────────── */

function AddressEditTab({ emp }: { emp: Employee }) {
  const mutation = useUpdateCurrentEmployee();
  const [editing, setEditing] = useState(false);
  const [street, setStreet] = useState(emp.employee_street ?? '');
  const [city, setCity] = useState(emp.employee_city ?? '');
  const [district, setDistrict] = useState(emp.employee_district ?? '');
  const [ward, setWard] = useState(emp.employee_ward ?? '');
  const [tmpStreet, setTmpStreet] = useState(emp.employee_temporary_street ?? '');
  const [saveError, setSaveError] = useState('');

  const handleSave = async () => {
    setSaveError('');
    try {
      await mutation.mutateAsync(buildPayload(emp, {
        employee_street: street,
        employee_city: city,
        employee_district: district,
        employee_ward: ward,
        employee_temporary_street: tmpStreet,
      }));
      setEditing(false);
    } catch {
      setSaveError('Failed to save. Please try again.');
    }
  };

  const handleCancel = () => {
    setStreet(emp.employee_street ?? '');
    setCity(emp.employee_city ?? '');
    setDistrict(emp.employee_district ?? '');
    setWard(emp.employee_ward ?? '');
    setTmpStreet(emp.employee_temporary_street ?? '');
    setSaveError('');
    setEditing(false);
  };

  if (!editing) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <InfoGrid items={[
          { label: 'Street', value: emp.employee_street, full: true },
          { label: 'Ward', value: emp.employee_ward },
          { label: 'District', value: emp.employee_district },
          { label: 'City', value: emp.employee_city },
          { label: 'Temporary address', value: emp.employee_temporary_street, full: true },
        ]} />
        <div>
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Edit address
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 480 }}>
      <Field label="Street (permanent address)">
        {(p) => (
          <Input {...p} value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Street" />
        )}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
        <Field label="Ward">
          {(p) => (
            <Input {...p} value={ward} onChange={(e) => setWard(e.target.value)} placeholder="Ward" />
          )}
        </Field>
        <Field label="District">
          {(p) => (
            <Input {...p} value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="District" />
          )}
        </Field>
      </div>
      <Field label="City">
        {(p) => (
          <Input {...p} value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
        )}
      </Field>
      <Field label="Temporary address">
        {(p) => (
          <Input {...p} value={tmpStreet} onChange={(e) => setTmpStreet(e.target.value)} placeholder="Temporary street address" />
        )}
      </Field>
      {saveError && (
        <Alert tone="danger" title={saveError} />
      )}
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
        <Button onClick={handleSave} loading={mutation.isPending}>
          Save
        </Button>
        <Button variant="secondary" onClick={handleCancel} disabled={mutation.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
